import { AppUtils } from '../../shared/utils/index';
import { BasicViewService, ViewConfiguration } from './BasicViewService';
import { BasicCampaignTrackingService } from './BasicCampaignTrackingService';
import { TimeService } from './TimeService';
import { PartnerViewModel } from '../models/view/Partner';
import { ChildModel } from '../models/view/Child';
import { AnredeArtApi } from '../models/generated/InterfacesAnredeArtApi';

/**
 * ViewService with membership specific extensions.
 */
export class MglViewService<T extends BasicAppDefinition> extends BasicViewService<T> {
	protected readonly trackingService: BasicCampaignTrackingService<T>;
	protected readonly timeService: TimeService;

	protected readonly firsNameBaseSel = "firstname_c";
	protected readonly titleBaseSel = "title_c";
	protected readonly lastNameBaseSel = "lastname_c";
	protected readonly addressExtBaseSel = "addressextension_c";
	protected readonly birthdayBaseSel = "birthday_c";
	protected readonly emailBaseSel = "email_c";
	protected readonly phonePfxBaseSel = "phonepre_c";
	protected readonly phoneBaseSel = "phone_c";
	protected readonly separateAddressBaseSel = "separateaddress_c";
	protected readonly streetBaseSel = "street_c";
	protected readonly zipBaseSel = "plz_c";
	protected readonly cityBaseSel = "city_c";
	protected readonly typeBaseSel = "type_c";

	constructor(options: BasicAppOptions<T>, loader: BasicLoader<T>) {
		super(null, options, loader);
		this.trackingService = new BasicCampaignTrackingService(options);
		this.timeService = new TimeService();
	}

	/**
	 * Overwrites the renderTemplate method to add the campaign tracking for membership applications.
	 */
	protected renderTemplate(template: string, config: ViewConfiguration<T>): void {
		super.renderTemplate(this.trackingService.setTracking(template), config);
	}

	public setChildDataToViewSuffixed(kind: ChildModel): void {
		const form = this.getForm();
		const $childFieldset = form.find(this.makeChildNameSelector(this.firsNameBaseSel, kind.Id)).closest("fieldset");

		$childFieldset.find(".js-child-label").toggleClass("h-hidden", kind.IsJuv);
		$childFieldset.find(".js-juv-label").toggleClass("h-hidden", !kind.IsJuv);

		this.setChildDataToView(kind, true);
	}

	private setChildDataToView(kind: ChildModel, suffixed = false): void {
		const form = this.getForm();

		if (kind.PersonalData.AnredeKennzeichen === AnredeArtApi.Herr || kind.PersonalData.AnredeKennzeichen === AnredeArtApi.Frau) {
			form.find(`[name="gender_c${suffixed ? '_' : ''}${kind.Id}"][value="${kind.Anrede}"]`).trigger('click');
		}
		if (suffixed) {
			form.find(this.makeChildNameSelector(this.typeBaseSel, kind.Id, suffixed)).val(kind.IsJuv ? "juv" : "child");
		}
		form.find(this.makeChildNameSelector(this.titleBaseSel, kind.Id, suffixed)).trigger('click');
		form.find(this.makeChildNameSelector(this.titleBaseSel, kind.Id, suffixed)).val(kind.PersonalData.Titel);
		form.find(this.makeChildNameSelector(this.firsNameBaseSel, kind.Id, suffixed)).val(kind.PersonalData.Vorname);
		form.find(this.makeChildNameSelector(this.lastNameBaseSel, kind.Id, suffixed)).val(kind.PersonalData.Nachname);
		form.find(this.makeChildNameSelector(this.addressExtBaseSel, kind.Id, suffixed)).val(kind.Adresse.Ergaenzungszeile);
		form.find(this.makeChildNameSelector(this.birthdayBaseSel, kind.Id, suffixed)).val(AppUtils.Format.isoDateToViewDate(kind.PersonalData.Geburtsdatum));
		form.find(this.makeChildNameSelector(this.emailBaseSel, kind.Id, suffixed)).val(kind.PersonalData.Email);
		form.find(this.makeChildNameSelector(this.phonePfxBaseSel, kind.Id, suffixed)).val(kind.PersonalData.TelefonVorwahl);
		form.find(this.makeChildNameSelector(this.phoneBaseSel, kind.Id, suffixed)).val(kind.PersonalData.TelefonNummer);
		if (kind.HatAbweichendeAdresse) {
			form.find(this.makeChildNameSelector(this.separateAddressBaseSel, kind.Id, suffixed)).trigger('click');
			form.find(this.makeChildNameSelector(this.streetBaseSel, kind.Id, suffixed)).val(kind.Adresse.StrasseHausnummer);
			form.find(this.makeChildNameSelector(this.zipBaseSel, kind.Id, suffixed)).val(kind.Adresse.Postleitzahl);
			form.find(this.makeChildNameSelector(this.cityBaseSel, kind.Id, suffixed)).val(kind.Adresse.Ort);
		}

	}

	/**
	 * Child array of valid children
	 *
	 */
	public getChildModels(formData: { [key: string]: any }, useOldCloneModel = false): ChildModel[] {
		const data = formData || this.getFormData();
		const childIds = useOldCloneModel ? this.getChildIdsOld() : this.getChildIds(),
			childs = new Array<ChildModel>();

		childIds.forEach((childId) => {
			const childModel = new ChildModel();
			childModel.Id = childId.replace("_", "");
			childModel.IsJuv = formData[this.typeBaseSel + childId] === "juv";
			childModel.PersonalData.AnredeKennzeichen = this.getAnredeKennzeichen(data[`gender_c${childId}`]);
			childModel.PersonalData.Titel = data[this.titleBaseSel + childId];
			childModel.PersonalData.Vorname = data[this.firsNameBaseSel + childId];
			childModel.PersonalData.Nachname = data[this.lastNameBaseSel + childId];
			childModel.PersonalData.Geburtsdatum = AppUtils.Format.viewDateToIsoDate(data[this.birthdayBaseSel + childId]);
			childModel.PersonalData.Email = data[this.emailBaseSel + childId];
			childModel.PersonalData.TelefonVorwahl = data[this.phonePfxBaseSel + childId];
			childModel.PersonalData.TelefonNummer = data[this.phoneBaseSel + childId];
			childModel.HatAbweichendeAdresse = !!data[this.separateAddressBaseSel + childId];
			childModel.Adresse.Ergaenzungszeile = data[this.addressExtBaseSel + childId];
			childModel.Adresse.StrasseHausnummer = data[this.streetBaseSel + childId];
			childModel.Adresse.Postleitzahl = data[this.zipBaseSel + childId];
			childModel.Adresse.Ort = data[this.cityBaseSel + childId];

			childs.push(childModel);
		});
		return childs;
	}

	public getChildModelsV2(formData: { [key: string]: any }): ChildModel[] {
		const data = formData || this.getFormData();

		const childIds = this.getChildIdsV2(),
			childs = new Array<ChildModel>();

		childIds.forEach((childId) => {
			const childModel = new ChildModel();
			childModel.Id = childId;
			childModel.PersonalData.AnredeKennzeichen = Number(data[`salutation_${childId}`]);
			childModel.PersonalData.Titel = data[`title_${childId}`];
			childModel.PersonalData.Vorname = data[`name_${childId}`];
			childModel.PersonalData.Nachname = data[`lastname_${childId}`];
			childModel.PersonalData.Geburtsdatum = AppUtils.Format.viewDateToIsoDate(data[`birth_${childId}`]);
			childModel.PersonalData.Email = data[`mail_${childId}`];
			childModel.PersonalData.TelefonVorwahl = data[`phone1_${childId}`];
			childModel.PersonalData.TelefonNummer = data[`phone0_${childId}`];
			childModel.HatAbweichendeAdresse = data[`addadress_${childId}`] === "2";
			childModel.Adresse.Ergaenzungszeile = data[`addressextension_${childId}`];
			childModel.Adresse.StrasseHausnummer = data[`otherstreet_${childId}`];
			childModel.Adresse.Postleitzahl = data[`otherplz_${childId}`];
			childModel.Adresse.Ort = data[`otherplace_${childId}`];

			if (data[`birth_${childId}`]) {
				const birthday = AppUtils.Format.viewDateToIsoDate(data[`birth_${childId}`]);
				const monthsDiff = this.timeService.getCurrentAgeInMonths(moment(birthday));
				childModel.IsJuv = monthsDiff > 215;
			} else {
				childModel.IsJuv = childId.indexOf("adultchild") > -1;
			}

			childs.push(childModel);
		});
		return childs;
	}

	public setChildDataToViewV2(kind: ChildModel, childId: string): void {
		const form = this.getForm();

		if (kind.PersonalData.AnredeKennzeichen) { //NOSONAR // AnredeKennzeichen may be of wrong type
			form.find(`[name="salutation_${childId}"][value="${kind.PersonalData.AnredeKennzeichen}"]`).trigger('click');
		}

		form.find(`[name="title_${childId}"]`).val(kind.PersonalData.Titel);
		form.find(`[name="name_${childId}"]`).val(kind.PersonalData.Vorname);
		form.find(`[name="lastname_${childId}"]`).val(kind.PersonalData.Nachname);
		form.find(`[name="addressextension_${childId}"]`).val(kind.Adresse.Ergaenzungszeile);
		form.find(`[name="birth_${childId}"]`).val(AppUtils.Format.isoDateToViewDate(kind.PersonalData.Geburtsdatum));
		form.find(`[name="mail_${childId}"]`).val(kind.PersonalData.Email);
		form.find(`[name="phone1_${childId}"]`).val(kind.PersonalData.TelefonVorwahl);
		form.find(`[name="phone0_${childId}"]`).val(kind.PersonalData.TelefonNummer);
		if (kind.HatAbweichendeAdresse) {
			form.find(`#addadress_j_${childId}`).trigger('click');
			form.find(`[name="otherstreet_${childId}"]`).val(kind.Adresse.StrasseHausnummer);
			form.find(`[name="otherplz_${childId}"]`).val(kind.Adresse.Postleitzahl);
			form.find(`[name="otherplace_${childId}"]`).val(kind.Adresse.Ort);
		}

	}

	protected makeChildNameSelector(baseName: string, id: string | number, suffixed = true): string {
		return `[name="${baseName}${suffixed ? '_' : ''}${id}"]`;
	}

	/**
	 * Gets the entered partner data from the form.
	 *
	 */
	public getPartnerModel(formData?: { [key: string]: any }): PartnerViewModel {
		const data = formData || this.getFormData();
		const partner = new PartnerViewModel();
		partner.Mitgliedsnummer = data.Mitgliedsnummer;
		if (!Number.isNaN(Number(data.AnredePartner))) {
			partner.PersonalData.AnredeKennzeichen = Number(data.AnredePartner);
		} else {
			partner.PersonalData.AnredeKennzeichen = this.getAnredeKennzeichen(data.AnredePartner || data.AnredePartner_0);
		}
		partner.PersonalData.Titel = data.TitelPartner || data.TitelPartner_0;
		partner.PersonalData.Vorname = data.VornamePartner || data.VornamePartner_0;
		partner.PersonalData.Nachname = data.NachnamePartner || data.NachnamePartner_0;
		partner.PersonalData.Geburtsdatum = AppUtils.Format.viewDateToIsoDate(data.GeburtsdatumPartner || data.GeburtsdatumPartner_0);
		partner.PersonalData.Email = data.EmailPartner || data.EmailPartner_0;
		partner.PersonalData.TelefonVorwahl = data.TelefonVorwahlPartner || data.TelefonVorwahlPartner_0;
		partner.PersonalData.TelefonNummer = data.TelefonNummerPartner || data.TelefonNummerPartner_0;
		return partner;
	}

	protected getChildIdsOld(): string[] {
		const childIds: string[] = [],
			childElements = this.$ctx.find('*[data-clone="child"]');
		for (let i = 0; i < childElements.length; i++) {
			const child = childElements.eq(i);
			const firstInput = child.find('input').eq(0);
			const name = firstInput.attr('name');
			childIds.push(name.split('_c')[1]);
		}
		return childIds;
	}

	private getChildIds(): string[] {
		const childIds: string[] = [];
		const firstnameFields = this.$ctx.find('form [data-clone-target="children"] [data-section="childdata"] [name^="firstname_c"]');
		for (let i = 0; i < firstnameFields.length; i++) {
			childIds.push(firstnameFields.eq(i).attr("name").split(this.firsNameBaseSel)[1]);
		}
		return childIds;
	}

	private getChildIdsV2(): string[] {
		const childIds: string[] = [];
		const firstnameFields = this.$ctx.find('form [name^="name_"]:visible');
		for (let i = 0; i < firstnameFields.length; i++) {
			childIds.push(firstnameFields.eq(i).attr("name").split('name_')[1]);
		}
		return childIds;
	}

	protected getAnredeKennzeichen(anredeValue: string): AnredeArtApi {
		if (anredeValue === 'Herr') {
			return AnredeArtApi.Herr;
		} else if (anredeValue === 'Frau') {
			return AnredeArtApi.Frau;
		} else if (anredeValue === 'Divers') {
			return AnredeArtApi.Familie;
		} else {
			return undefined;
		}
	}
}
